/**
 * The Gauge — the post-match temperature of the fanbase. The dial SVG is the
 * design; never a charting library. Renders honest empty states: no average
 * is ever fabricated.
 */
import { useEffect, useRef, useState } from 'react';
import { GAUGE_EMPTY, fetchGauge, postGauge } from '../lib/api';
import { broadcastFanReaction } from '../lib/intents';
import type { GaugeSummary, Mood } from '../lib/types';
import { Rule } from './chrome';
import { ShareButton } from './ShareButton';
import { matchLink } from '../lib/share';
import { ParticipationGate, useParticipationConsent } from './ParticipationGate';
import { recordGrowth } from '../lib/growth';

const MOODS: Array<{ id: Mood; label: string }> = [
  { id: 'buzzing', label: 'Buzzing' },
  { id: 'relieved', label: 'Relieved' },
  { id: 'anxious', label: 'Anxious' },
  { id: 'frustrated', label: 'Frustrated' },
];

export const GaugeDial = ({ score, sampleSize }: { score: number; sampleSize?: number }) => {
  const cx = 150;
  const cy = 148;
  const rInner = 88;
  const rotation = (score / 5 - 1) * 90;
  const needleRadians = rotation * Math.PI / 180;
  const needleLength = rInner + 4;
  const needleX = cx + needleLength * Math.sin(needleRadians);
  const needleY = cy - needleLength * Math.cos(needleRadians);

  const arcPath = (radius: number, startDeg: number, endDeg: number) => {
    const s = (startDeg * Math.PI) / 180;
    const e = (endDeg * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(s);
    const y1 = cy - radius * Math.sin(s);
    const x2 = cx + radius * Math.cos(e);
    const y2 = cy - radius * Math.sin(e);
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${endDeg - startDeg > 180 ? 1 : 0} 0 ${x2} ${y2}`;
  };

  const verdict =
    sampleSize !== undefined && sampleSize < 5 ? 'Early read' :
    score >= 8.5 ? 'Euphoric Gunners' :
    score >= 7 ? 'Optimistic Gunners' :
    score >= 5 ? 'Cautious Gunners' :
    score >= 3 ? 'Worried Gunners' : 'Dark Days';

  return (
    <div className="gauge-dial">
      <svg className="gauge-dial__svg" viewBox="0 0 300 165">
        <defs>
          <linearGradient id="arcGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3a2020" />
            <stop offset="40%" stopColor="#7a3010" />
            <stop offset="70%" stopColor="#EF0107" />
            <stop offset="100%" stopColor="#B8963E" />
          </linearGradient>
          <filter id="glowNeedle2">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={arcPath(rInner + 14, 0, 180)} fill="none" stroke="#EDE8E2" strokeWidth="26" strokeLinecap="round" />
        <path d={arcPath(rInner + 14, 0, 180)} fill="none" stroke="url(#arcGrad2)" strokeWidth="22" strokeLinecap="butt" opacity="0.85" />
        {[0, 18, 36, 54, 72, 90, 108, 126, 144, 162, 180].map((deg, i) => {
          const rad = (deg * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={cx + (rInner + 3) * Math.cos(rad)}
              y1={cy - (rInner + 3) * Math.sin(rad)}
              x2={cx + (rInner + 25) * Math.cos(rad)}
              y2={cy - (rInner + 25) * Math.sin(rad)}
              stroke="#F8F4EE"
              strokeWidth={i % 2 === 0 ? 2 : 1}
              opacity="0.8"
            />
          );
        })}
        <text x={cx - (rInner + 16)} y={cy + 6} className="gauge-axis" textAnchor="middle">0</text>
        <text x={cx + (rInner + 16)} y={cy + 6} className="gauge-axis" textAnchor="middle">10</text>
        <line x1={cx} y1={cy + 12} x2={needleX} y2={needleY} stroke="var(--gold-light)" strokeWidth="3" strokeLinecap="round" filter="url(#glowNeedle2)" />
        <line x1={cx} y1={cy + 12} x2={cx} y2={cy + 20} stroke="var(--gold)" strokeWidth="4" strokeLinecap="round" opacity="0.45" />
        <circle cx={cx} cy={cy} r="9" fill="var(--raised)" stroke="var(--gold)" strokeWidth="2" />
        <circle cx={cx} cy={cy} r="4" fill="var(--gold)" />
      </svg>
      <div className="gauge-score">
        <div className="gauge-score-num">
          {score.toFixed(1)}
          <span className="gauge-score-of">/10</span>
        </div>
        <div className="gauge-verdict">{verdict}</div>
      </div>
    </div>
  );
};

/**
 * Full gauge panel for one match: community dial + my rating + mood + moment.
 */
export const GaugePanel = ({ matchId, matchLabel, writable = true }: { matchId: string; matchLabel: string; writable?: boolean }) => {
  const [gauge, setGauge] = useState<GaugeSummary>(GAUGE_EMPTY);
  const gaugeRef = useRef<GaugeSummary>(GAUGE_EMPTY);
  const confirmedGaugeRef = useRef<GaugeSummary>(GAUGE_EMPTY);
  const writeGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const matchRef = useRef(matchId);
  matchRef.current = matchId;
  const [hydrated, setHydrated] = useState(false);
  const [moment, setMoment] = useState('');
  const live = useRef(true);
  const [participationAllowed, acceptParticipation] = useParticipationConsent();
  const [gateOpen, setGateOpen] = useState(false);
  const firstRatingRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    live.current = true;
    setHydrated(false);
    const load = ++loadGeneration.current;
    const generation = writeGeneration.current;
    fetchGauge(matchId).then(({ gauge }) => {
      if (live.current && load === loadGeneration.current && generation === writeGeneration.current) {
        gaugeRef.current = gauge;
        confirmedGaugeRef.current = gauge;
        setGauge(gauge);
        setHydrated(true);
        setMoment(gauge.mine?.moment ?? '');
      }
    });
    return () => {
      live.current = false;
    };
  }, [matchId]);

  useEffect(() => {
    const reconcile = (event: Event) => {
      if ((event as CustomEvent<{ kind?: string }>).detail?.kind !== 'gauge') return;
      const reconcileMatch = matchId;
      const load = loadGeneration.current;
      const write = writeGeneration.current;
      void fetchGauge(matchId).then(({ gauge }) => {
        if (live.current && reconcileMatch === matchRef.current && load === loadGeneration.current && write === writeGeneration.current) {
          gaugeRef.current = gauge;
          confirmedGaugeRef.current = gauge;
          setGauge(gauge);
          setHydrated(true);
        }
      });
    };
    window.addEventListener('coyg:write-persistence-failed', reconcile);
    return () => window.removeEventListener('coyg:write-persistence-failed', reconcile);
  }, [matchId]);

  const myRating = gauge.mine?.rating ?? 0;
  const myMood = gauge.mine?.mood ?? null;

  const patch = (p: { rating?: number | null; mood?: Mood | null; moment?: string | null }) => {
    if (!hydrated) return;
    const write = ++writeGeneration.current;
    const writeMatch = matchId;
    const previous = gaugeRef.current;
    const mine = {
      rating: p.rating !== undefined ? p.rating : (previous.mine?.rating ?? null),
      mood: p.mood !== undefined ? p.mood : (previous.mine?.mood ?? null),
      moment: p.moment !== undefined ? p.moment : (previous.mine?.moment ?? null),
    };
    const optimistic = { ...previous, mine };
    gaugeRef.current = optimistic;
    setGauge(optimistic);
    postGauge(matchId, mine).then((res) => {
      if (live.current && write === writeGeneration.current && writeMatch === matchRef.current) {
        if (res) confirmedGaugeRef.current = res;
        gaugeRef.current = res ?? confirmedGaugeRef.current;
        setGauge(res ?? confirmedGaugeRef.current);
      }
    });
    recordGrowth('gauge', 'match');
    broadcastFanReaction(matchId, 'gauge');
  };

  return (
    <section className="gauge-panel">
      {gauge.avg !== null ? (
        <>
          <GaugeDial score={gauge.avg} sampleSize={gauge.count} />
          <p className="gauge-count">
            {gauge.count.toLocaleString()} Gunner{gauge.count === 1 ? '' : 's'} rated
          </p>
        </>
      ) : (
        <div className="gauge-empty">
          <GaugeDial score={5} sampleSize={0} />
          <p className="gauge-count">{gauge.mine ? 'Your rating is saved privately. The crowd reading is not available yet.' : writable ? 'Add your private rating to leave a matchday reaction.' : 'No crowd reading is available for this match.'}</p>
        </div>
      )}

      {!writable ? <p className="gauge-count">Community reactions are closed for this match.</p> : !hydrated ? <p className="gauge-count" role="status">Loading your Gauge…</p> : !participationAllowed ? (
        <button className="gauge-start" type="button" onClick={() => setGateOpen(true)}>Add your rating</button>
      ) : <>
      <div className="mood-grid">
        {MOODS.map((m) => (
          <button
            key={m.id}
            className={`mood-cell${myMood === m.id ? ' active' : ''}`}
            aria-pressed={myMood === m.id}
            onClick={() => patch({ mood: myMood === m.id ? null : m.id })}
          >
            <span className="mood-pct">{gauge.moodAggregateAvailable ? `${gauge.moods[m.id] ?? 0}%` : '—'}</span>
            <span className="mood-label">{m.label}</span>
          </button>
        ))}
      </div>

      <Rule label="Your rating" />
      <div className="rating-row">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button
            key={n}
            ref={n === 1 ? firstRatingRef : undefined}
            className={`rating-cell${myRating === n ? ' active' : ''}${myRating > 0 && n <= myRating ? ' lit' : ''}`}
            aria-pressed={myRating === n}
            onClick={() => patch({ rating: myRating === n ? null : n })}
          >
            {n}
          </button>
        ))}
      </div>

      <input
        className="moment-input"
        aria-label="Moment of the match"
        value={moment}
        maxLength={140}
        onChange={(e) => setMoment(e.target.value)}
        onBlur={() => patch({ moment: moment.trim() || null })}
        onKeyDown={(e) => e.key === 'Enter' && patch({ moment: moment.trim() || null })}
        placeholder="Moment of the match…"
      />
      </>}

      {gateOpen && (
        <ParticipationGate
          action="add your rating"
          onAccepted={acceptParticipation}
          onSuccessFocus={() => firstRatingRef.current?.focus()}
          onClose={() => setGateOpen(false)}
        />
      )}

      {(gauge.avg !== null || myRating > 0) && (
        <div className="gauge-share">
          <ShareButton
            ghost
            label="Share the gauge"
            moment={{
              title: 'COYG — fan gauge',
              text:
                gauge.avg !== null
                  ? `Gunners rated ${matchLabel} ${gauge.avg.toFixed(1)}/10${myRating ? ` — I gave it ${myRating}` : ''}.`
                  : `I rated ${matchLabel} ${myRating}/10 on COYG.`,
              url: matchLink(matchId),
              card: {
                eyebrow: 'MY MATCHDAY GAUGE',
                headline: myRating ? `${myRating}/10` : `${gauge.avg!.toFixed(1)}/10`,
                detail: `${matchLabel}${gauge.avg !== null ? ` · Crowd: ${gauge.avg.toFixed(1)}/10` : ''}`,
                stamp: myRating ? 'MY GAUGE · COYG.ORG' : 'THE GAUGE · COYG.ORG',
                personal: myRating > 0,
              },
            }}
          />
        </div>
      )}
    </section>
  );
};
